import type React from 'react';

import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useTranslate, useCanAccess, Resource as RaResource } from 'react-admin';
import { Navigate, useMatch } from 'react-router-dom';

import type { ResourceProps } from 'react-admin';

const NoAccess = (): React.ReactElement => {
  const translate = useTranslate();

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        {translate('ra.page.access_denied')}
      </Typography>
      <Alert variant='outlined' severity='error' sx={{ mb: 2 }}>
        {translate('ra.notification.not_authorized')}
      </Alert>
    </Paper>
  );
};

/**
 * RBAC Resource wrapper that checks permissions for the current sub-route.
 * Renders a custom "no access" component if the user lacks permission.
 */
export const Resource = ({
  name,
  list,
  show,
  hasCreate,
  hasEdit,
  hasShow,
  options,
  ...props
}: ResourceProps): React.ReactElement => {
  // Try to match each possible route pattern
  const matchCreate = useMatch(`/${name}/create`);
  const matchShow = useMatch(`/${name}/:id/show`);
  const matchEdit = useMatch(`/${name}/:id`);

  let action: string;
  if (matchCreate) {
    action = 'create';
  } else if (matchEdit && matchEdit.params.id !== 'create') {
    action = 'edit';
  } else if (matchShow) {
    action = 'show';
  } else {
    action = 'list';
  }

  const { isPending, canAccess } = useCanAccess({
    action,
    resource: name,
  });

  const { isPending: isPendingList, canAccess: canAccessList } = useCanAccess({
    action: 'list',
    resource: name,
  });

  const { isPending: isPendingCreate, canAccess: canAccessCreate } = useCanAccess({
    action: 'create',
    resource: name,
  });

  const { isPending: isPendingEdit, canAccess: canAccessEdit } = useCanAccess({
    action: 'edit',
    resource: name,
  });

  const { isPending: isPendingShow, canAccess: canAccessShow } = useCanAccess({
    action: 'show',
    resource: name,
  });

  // Wait for permissions to load - prevents flash of "unauthorized"
  if (isPending || isPendingList || isPendingCreate || isPendingEdit || isPendingShow) {
    return (
      <Box height='50%' display='flex' alignItems='center' justifyContent='center'>
        <CircularProgress />
      </Box>
    );
  }

  // Users who can view but not edit land on the readonly show view instead of an access-denied page.
  // Gated on a registered show view or an explicit options.redirectEditToShow opt-in — many resources
  // set hasShow for list-embedded dialogs and must keep their access-denied behavior on the edit route.
  if (
    action === 'edit' &&
    !canAccess &&
    matchEdit != null &&
    (show != null || options?.redirectEditToShow === true) &&
    canAccessShow === true
  ) {
    return <Navigate to={`/${name}/${matchEdit.params.id ?? ''}/show`} replace />;
  }

  // Check basic resource permission
  if (!canAccess) {
    return <NoAccess />;
  }

  return (
    <RaResource
      name={name}
      list={canAccessList ? list : undefined}
      show={canAccessShow ? show : undefined}
      hasCreate={canAccessCreate ? hasCreate : undefined}
      hasEdit={canAccessEdit ? hasEdit : undefined}
      hasShow={canAccessShow ? hasShow : undefined}
      options={options}
      {...props}
    />
  );
};

Resource.raName = 'Resource';

Resource.registerResource = ({
  create,
  edit,
  icon,
  list,
  name,
  options,
  show,
  recordRepresentation,
  hasCreate,
  hasEdit,
  hasShow,
}: ResourceProps) => ({
  name,
  options,
  hasList: !!list,
  hasCreate: !!create || !!hasCreate,
  hasEdit: !!edit || !!hasEdit,
  hasShow: !!show || !!hasShow,
  icon,
  recordRepresentation,
});
